import { auteurPourRequete } from './portees.js';
import {
  type DocumentData,
  type Firestore,
  type QueryConstraint,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit as limitTo,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  CONTENT_CHARGE_MAX,
  CONTENT_CUSTOM_FIELDS_MAX,
  CONTENT_PARTS_MAX,
  CONTENT_PART_MAX,
  CONTENT_PREVIEW_MAX,
  ContentCreateInputSchema,
  ContentPartSchema,
  ContentSchema,
  ContentUpdateInputSchema,
  accesACreer,
  accesDe,
  contentCoreFields,
  motsNormalises,
  octetsUtf8,
  prefixesDeRecherche,
  CONTENT_SEARCH_PREFIX_MAX,
  type Content,
  type ContentCreateInput,
  type ContentEncoding,
  type ContentUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type AccesDeListe,
  type CountOptions,
  type ListOptions,
  CursorLostError,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';
import type { ObserveOps } from './observe-ops.js';
import { type AccesOps, type MaPersonne, cadrageAcces, createAccesOps } from './acces-par-fiche.js';

/**
 * Repository de la famille `contents` : le contenu qu'edite une surface
 * Canvas (un dessin, une carte mentale, un plan de salle). Le modele et ses
 * deux niveaux (la FICHE sans charge, les MORCEAUX en sous-collection) sont
 * expliques dans `packages/data-model/src/content.ts` ; la doctrine d'usage,
 * pour qui construit une surface, dans `docs/DONNEES-SDK.md`, section
 * « Les contenus ».
 *
 * Les verbes standard sont ceux des autres familles (`list`, `count`, `get`,
 * `update`, `archive`, `unarchive`, `remove`) et ne touchent JAMAIS la charge.
 * Ce que la famille ajoute :
 *
 *  - `create`      : la fiche et sa charge initiale en UN lot ; un id impose
 *                    n'ecrase jamais un contenu existant (`ContenuExistant`) ;
 *  - `loadContent` : la charge complete, morceaux reassembles et decodes,
 *                    garantie coherente (sinon relue, puis `ContenuIncoherent`) ;
 *  - `saveContent` : la charge en UN lot, avec la revision attendue. Refus
 *                    NOMMES : `ConflitDeVersion`, `EcritureRefusee` (et sa
 *                    raison), `ChargeTropLourde` ;
 *  - `copy`        : une copie complete en UN lot, rejouable sans doublon
 *                    quand on lui donne son id (`newId`) ;
 *  - `page`        : la galerie, page par page, avec recherche serveur sur le
 *                    nom : jamais la charge ;
 *  - `observe`     : la fiche en direct (`observe-ops.ts`), pour voir passer la
 *                    revision d'un autre onglet sans relire la charge.
 *
 * CE QUI N'EST PAS ICI, et vit dans les lots suivants du kit : l'enregistrement
 * automatique, le brouillon local, l'annonce d'un conflit et sa copie
 * recuperable (lot 3, surfaces Canvas et sauvegarde) ; les binaires (lot 2,
 * Cloud Storage).
 */

export type { ContentCreateInput };

/** Nom de la collection Firestore de la famille content. */
export const CONTENTS_COLLECTION = 'contents';
/** Nom de la sous-collection des morceaux : `contents/{id}/parts/{i}`. */
export const CONTENT_PARTS_COLLECTION = 'parts';

/* -------------------------------------------------------------------------- */
/* Les refus que l'appelant doit reconnaitre                                  */
/* -------------------------------------------------------------------------- */
//
// Tous se reconnaissent par leur NOM (`estConflitDeVersion`...), jamais par
// `instanceof` : une erreur qui traverse deux bundles n'est plus toujours
// l'instance de sa classe.

/**
 * QUELQU'UN A ECRIT ENTRE-TEMPS. Le lot est parti avec la revision `attendue`,
 * la base en porte une autre : les rules l'ont refuse, RIEN n'a ete ecrit.
 * L'appelant relit (`loadContent`) et decide ; le travail local est intact.
 */
export class ConflitDeVersion extends Error {
  override readonly name = 'ConflitDeVersion';
  constructor(
    readonly contentId: string,
    readonly attendue: number,
    readonly enBase: number,
  ) {
    super(
      `Le contenu ${contentId} a ete modifie ailleurs (revision ${enBase} en base, ` +
        `${attendue} attendue) : rien n'a ete ecrit.`,
    );
  }
}

/** La charge stockee depasse `CONTENT_CHARGE_MAX` : rien n'est parti. */
export class ChargeTropLourde extends Error {
  override readonly name = 'ChargeTropLourde';
  constructor(readonly octets: number) {
    super(
      `Contenu trop lourd pour etre enregistre : ${octets} octets stockes pour un plafond de ` +
        `${CONTENT_CHARGE_MAX} (${CONTENT_PARTS_MAX} morceaux de ${CONTENT_PART_MAX}).`,
    );
  }
}

/**
 * Pourquoi une ecriture est refusee POUR DE BON (une nouvelle tentative ne
 * changera rien, cf. `EcritureRefusee`) :
 *  - `supprime`    : le contenu a ete supprime (`deletedAt`) ;
 *  - `archive`     : le contenu est archive : sa charge ne s'ecrit plus tant
 *                    qu'il n'est pas desarchive ;
 *  - `introuvable` : le contenu n'existe pas ;
 *  - `droits`      : les rules refusent, et rien d'autre ne l'explique (acces
 *                    retire, porte fermee, ou contenu devenu illisible) ;
 *  - `format`      : l'ecriture ferait DESCENDRE la version du format (un
 *                    onglet reste sur un ancien moteur) : refuse avant de partir.
 */
export type RaisonDeRefus = 'supprime' | 'archive' | 'introuvable' | 'droits' | 'format';

/**
 * UN REFUS QU'UNE NOUVELLE TENTATIVE NE REPARERA PAS. L'appelant s'arrete,
 * garde le travail sur le poste, et le dit. Pas `RefusDEcriture` : ce nom est
 * deja celui du refus des vues de `@taki/ui`.
 */
export class EcritureRefusee extends Error {
  override readonly name = 'EcritureRefusee';
  constructor(
    readonly contentId: string,
    readonly raison: RaisonDeRefus,
  ) {
    super(
      {
        supprime: `Le contenu ${contentId} a ete supprime : rien n'a ete ecrit.`,
        archive: `Le contenu ${contentId} est archive : rien n'a ete ecrit.`,
        introuvable: `Le contenu ${contentId} n'existe pas : rien n'a ete ecrit.`,
        droits: `Ecriture du contenu ${contentId} refusee par les Security Rules (droits) : rien n'a ete ecrit.`,
        format:
          `Le contenu ${contentId} est deja a un format plus recent que celui de cet ecran : ` +
          'rien n\'a ete ecrit, rechargez la page.',
      }[raison],
    );
  }
}

/**
 * UN ID IMPOSE EST DEJA PRIS. `create` avec `options.id`, ou `copy` vers un id
 * qui porte autre chose que la copie demandee : rien n'a ete ecrit, et le
 * contenu existant est intact (les rules refusent aussi l'ecrasement).
 */
export class ContenuExistant extends Error {
  override readonly name = 'ContenuExistant';
  constructor(readonly contentId: string) {
    super(`Le contenu ${contentId} existe deja : rien n'a ete ecrit.`);
  }
}

/**
 * UN CONTENU NE S'ANCRE PAS. La forme `ancre` de l'acces par fiche ne vaut que
 * pour les conversations (le chatter) : une liste ou un compte de contenus qui
 * demande `acces: { ancre }` est refuse ici, avant toute requete.
 */
export class ContenuNonAncrable extends Error {
  override readonly name = 'ContenuNonAncrable';
  constructor() {
    super(
      "Un contenu ne s'ancre pas : `acces: { ancre }` ne vaut que pour les conversations. " +
        "Un contenu est equipe ou prive.",
    );
  }
}

/** Refuse l'option `acces: { ancre }` d'une liste ou d'un compte de contenus (`ContenuNonAncrable`). Pure. */
export function refuserAncre(acces: AccesDeListe | undefined): void {
  if (typeof acces === 'object' && acces !== null && 'ancre' in acces) throw new ContenuNonAncrable();
}

/**
 * LA FICHE NE SE LIT PAS : elle n'existe pas, ou les droits ne l'ouvrent plus.
 * Les rules ne distinguent pas les deux (lire un document absent y est un
 * refus, pas un vide), et le SDK ne pretend pas le faire.
 */
export class ContenuIllisible extends Error {
  override readonly name = 'ContenuIllisible';
  constructor(readonly contentId: string) {
    super(`Le contenu ${contentId} est introuvable, ou n'est plus accessible.`);
  }
}

/**
 * LES MORCEAUX NE RACONTENT PAS LA MEME REVISION que la fiche, apres
 * `RELECTURES_MAX` lectures. Les rules empechent d'ECRIRE un tel etat : on ne
 * le voit donc qu'en lisant pendant qu'un autre ecrit sans cesse. Rien n'est
 * recolle au hasard : un contenu qui n'a jamais existe ne sort jamais d'ici.
 */
export class ContenuIncoherent extends Error {
  override readonly name = 'ContenuIncoherent';
  constructor(readonly contentId: string) {
    super(
      `Le contenu ${contentId} change pendant sa lecture (ses morceaux ne portent pas la revision ` +
        `de sa fiche apres ${RELECTURES_MAX} lectures) : reessayez dans un instant.`,
    );
  }
}

/** La reconnaissance par le nom, commune aux refus ci-dessus. */
function parNom<T extends Error>(nom: string) {
  return (erreur: unknown): erreur is T => erreur instanceof Error && erreur.name === nom;
}
export const estConflitDeVersion = parNom<ConflitDeVersion>('ConflitDeVersion');
export const estChargeTropLourde = parNom<ChargeTropLourde>('ChargeTropLourde');
export const estEcritureRefusee = parNom<EcritureRefusee>('EcritureRefusee');
export const estContenuExistant = parNom<ContenuExistant>('ContenuExistant');
export const estContenuIllisible = parNom<ContenuIllisible>('ContenuIllisible');
export const estContenuIncoherent = parNom<ContenuIncoherent>('ContenuIncoherent');
export const estContenuNonAncrable = parNom<ContenuNonAncrable>('ContenuNonAncrable');

/** Ce que la relecture d'une fiche a rendu, apres un refus des rules. */
export type RelectureApresRefus =
  /** Sa lecture est refusee elle aussi : absente, ou droits retires. */
  | { etat: 'illisible' }
  /** Elle a ete lue : ce qu'elle porte. */
  | { etat: 'lu'; version: unknown; archivedAt: unknown; deletedAt: unknown };

/**
 * NOMMER UN `permission-denied`, pur. Le refus du verrou ressemble a tout autre
 * refus des rules : c'est la RELECTURE de la fiche qui les separe. L'ordre
 * compte : un contenu SUPPRIME est dit supprime meme si sa revision a bouge
 * (relire ne servirait a rien) ; une revision qui a bouge est un conflit ; un
 * contenu archive refuse toute charge ; le reste ne peut etre que les droits.
 */
export function nommerLeRefus(
  contentId: string,
  attendue: number,
  relecture: RelectureApresRefus,
): ConflitDeVersion | EcritureRefusee {
  if (relecture.etat === 'illisible') return new EcritureRefusee(contentId, 'droits');
  if (relecture.deletedAt != null) return new EcritureRefusee(contentId, 'supprime');
  if (typeof relecture.version === 'number' && relecture.version !== attendue) {
    return new ConflitDeVersion(contentId, attendue, relecture.version);
  }
  if (relecture.archivedAt != null) return new EcritureRefusee(contentId, 'archive');
  return new EcritureRefusee(contentId, 'droits');
}

/* -------------------------------------------------------------------------- */
/* Les fonctions pures : encoder, decouper, reassembler, migrer               */
/* -------------------------------------------------------------------------- */

/** L'id (le chemin relatif) du morceau `index` d'un contenu. */
export function cheminDeMorceau(contentId: string, index: number): string {
  return `${CONTENTS_COLLECTION}/${contentId}/${CONTENT_PARTS_COLLECTION}/${index}`;
}

/**
 * DECOUPE UNE CHARGE STOCKEE en morceaux de `max` octets UTF-8 au plus, sans
 * jamais couper un caractere en deux (ni une paire de substitution). Une charge
 * VIDE rend ZERO morceau : un contenu vide n'a pas de sous-collection.
 */
export function decouperCharge(stockee: string, max: number = CONTENT_PART_MAX): string[] {
  if (!Number.isInteger(max) || max < 4) {
    throw new Error(`decouperCharge : \`max\` doit etre un entier d'au moins 4 octets (recu : ${String(max)}).`);
  }
  if (stockee.length === 0) return [];
  if (stockee.length * 3 <= max || octetsUtf8(stockee) <= max) return [stockee];
  const morceaux: string[] = [];
  let debut = 0;
  let octets = 0;
  for (let i = 0; i < stockee.length; ) {
    const code = stockee.charCodeAt(i);
    let largeur = 1;
    let poids: number;
    if (code < 0x80) poids = 1;
    else if (code < 0x800) poids = 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < stockee.length) {
      const suivant = stockee.charCodeAt(i + 1);
      if (suivant >= 0xdc00 && suivant <= 0xdfff) {
        poids = 4;
        largeur = 2;
      } else poids = 3;
    } else poids = 3;
    if (octets + poids > max) {
      morceaux.push(stockee.slice(debut, i));
      debut = i;
      octets = 0;
    }
    octets += poids;
    i += largeur;
  }
  morceaux.push(stockee.slice(debut));
  return morceaux;
}

/**
 * DECOUPE UNE CHARGE et verifie qu'elle tient dans `CONTENT_PARTS_MAX`
 * morceaux. Le test porte sur le NOMBRE de morceaux, apres la decoupe, et pas
 * seulement sur les octets : pour ne pas couper un caractere, un morceau peut
 * rester jusqu'a trois octets sous son plafond, et une charge a quelques octets
 * de `CONTENT_CHARGE_MAX` peut demander un neuvieme morceau, que les rules
 * refuseraient (et que le SDK nommerait alors, a tort, un refus de droits).
 */
export function decouperEnMorceaux(stockee: string): string[] {
  const octets = octetsUtf8(stockee);
  if (octets > CONTENT_CHARGE_MAX) throw new ChargeTropLourde(octets);
  const morceaux = decouperCharge(stockee);
  if (morceaux.length > CONTENT_PARTS_MAX) throw new ChargeTropLourde(octets);
  return morceaux;
}

/** Ce qu'il faut d'un morceau lu pour le reassembler. */
export interface MorceauLu {
  index: number;
  version: number;
  payload: string;
}

/**
 * RECOLLE LA CHARGE STOCKEE d'une fiche, pure. Rend `null` quand la lecture est
 * INCOHERENTE : un morceau manque, ou n'a pas la revision de la fiche. Ca
 * n'arrive que si quelqu'un ecrit entre la lecture de la fiche et celle de ses
 * morceaux (les rules refusent d'ecrire un etat incoherent) : l'appelant relit.
 * Un morceau au-dela de `parts` est ignore (les rules le font disparaitre dans
 * le lot qui retrecit le contenu ; le voir ici veut dire qu'on a lu entre deux).
 */
export function assemblerCharge(
  fiche: { parts: number; version: number },
  morceaux: readonly MorceauLu[],
): string | null {
  const parIndex = new Map(morceaux.map((morceau) => [morceau.index, morceau]));
  const textes: string[] = [];
  for (let index = 0; index < fiche.parts; index += 1) {
    const morceau = parIndex.get(index);
    if (morceau === undefined || morceau.version !== fiche.version) return null;
    textes.push(morceau.payload);
  }
  return textes.join('');
}

/**
 * Comment `saveContent` et `create` stockent le texte :
 *  - `auto`    : en clair jusqu'a `SEUIL_EN_CLAIR` octets, compresse au-dela
 *                (defaut) ;
 *  - `deflate` : toujours compresse ;
 *  - `aucune`  : jamais compresse ; en clair jusqu'au seuil, en base64url
 *                au-dela (un gros texte ne voyage jamais en clair, cf.
 *                `CONTENT_ENCODINGS`, @taki/data-model).
 */
export type Compression = 'auto' | 'deflate' | 'aucune';

/**
 * Jusqu'a ce poids, un texte reste en clair (lisible dans la console
 * Firebase) : meme gonfle par le transport (six fois au pire), il reste
 * loin de tout plafond. Au-dela, il part en base64url.
 */
export const SEUIL_EN_CLAIR = 32_000;
/** @deprecated nom d'avant : `SEUIL_EN_CLAIR`. */
export const SEUIL_DE_COMPRESSION = SEUIL_EN_CLAIR;

/**
 * Des octets en base64URL, par tranches (un seul `String.fromCharCode` deborde
 * la pile) : `-` et `_` au lieu de `+` et `/`, sans `=` final. Les 64 signes
 * voyagent tels quels dans une requete encodee URL.
 */
export function versBase64Url(octets: Uint8Array): string {
  let binaire = '';
  const tranche = 0x8000;
  for (let i = 0; i < octets.length; i += tranche) {
    binaire += String.fromCharCode(...octets.subarray(i, i + tranche));
  }
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function depuisBase64Url(texte: string): Uint8Array<ArrayBuffer> {
  const classique = texte.replace(/-/g, '+').replace(/_/g, '/');
  const binaire = atob(classique + '='.repeat((4 - (classique.length % 4)) % 4));
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return octets;
}

/** Fait passer des octets par un flux de (de)compression. */
async function parFlux(octets: Uint8Array<ArrayBuffer>, flux: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const sortie = new Blob([octets]).stream().pipeThrough(flux);
  return new Uint8Array(await new Response(sortie).arrayBuffer());
}

/**
 * ENCODE UN TEXTE pour le stockage. Rend la charge stockee, son encodage et la
 * taille du texte DECODE (ce que l'utilisateur a produit, `size`).
 */
export async function encoderCharge(
  texte: string,
  compression: Compression = 'auto',
): Promise<{ stockee: string; encoding: ContentEncoding; size: number }> {
  const size = octetsUtf8(texte);
  if (size === 0) return { stockee: '', encoding: 'utf8', size };
  const enClair = size <= SEUIL_EN_CLAIR;
  if (compression === 'deflate' || (compression === 'auto' && !enClair)) {
    const compresse = await parFlux(new TextEncoder().encode(texte), new CompressionStream('deflate'));
    return { stockee: versBase64Url(compresse), encoding: 'deflate-base64url', size };
  }
  if (enClair) return { stockee: texte, encoding: 'utf8', size };
  return { stockee: versBase64Url(new TextEncoder().encode(texte)), encoding: 'base64url', size };
}

/** DECODE une charge stockee (l'inverse d'`encoderCharge`). */
export async function decoderCharge(stockee: string, encoding: ContentEncoding): Promise<string> {
  if (encoding === 'utf8') return stockee;
  if (encoding === 'base64url') return new TextDecoder('utf-8', { fatal: true }).decode(depuisBase64Url(stockee));
  if (encoding === 'deflate-base64url') {
    const octets = await parFlux(depuisBase64Url(stockee), new DecompressionStream('deflate'));
    return new TextDecoder('utf-8', { fatal: true }).decode(octets);
  }
  throw new Error(`Encodage de contenu inconnu : « ${String(encoding)} ».`);
}

/**
 * LE FORMAT D'UNE CHARGE, tel qu'un moteur le declare. La REVISION (`version`)
 * dit « combien de sauvegardes » ; le FORMAT dit « quelle forme a la charge ».
 * Une mise a jour du moteur (npm) peut changer la forme : sans ce contrat, un
 * vieux contenu s'ouvrirait comme s'il etait neuf, et se casserait.
 *
 *  - `version`     : le format que CE code ecrit (entier, 1 au depart) ;
 *  - `lire`        : lit et VALIDE un texte au format courant, rend la valeur
 *                    ou LEVE (un `JSON.parse` suivi d'un cast n'est pas une
 *                    validation : un schema Zod l'est) ;
 *  - `migrations`  : `migrations[n]` transforme un texte au format `n` en texte
 *                    au format `n + 1`. Une par marche, jamais de saut.
 */
export interface FormatDeContenu<T> {
  version: number;
  lire(texte: string): T;
  migrations?: Readonly<Record<number, (texte: string) => string>>;
}

/**
 * LE CONTENU EST A UN FORMAT PLUS RECENT que celui du code qui l'ouvre (un
 * onglet reste ouvert pendant une mise a jour). Il se MONTRE eventuellement,
 * il ne s'ECRIT pas : les rules refuseraient de faire descendre son format, et
 * l'ecran doit proposer de recharger la page.
 */
export class FormatTropRecent extends Error {
  override readonly name = 'FormatTropRecent';
  constructor(
    readonly enBase: number,
    readonly connu: number,
  ) {
    super(`Contenu au format ${enBase}, ce code ne connait que le format ${connu} : rechargez la page.`);
  }
}

/** Une marche de migration manque, ou la charge ne se lit pas au format annonce. */
export class FormatIllisible extends Error {
  override readonly name = 'FormatIllisible';
  constructor(
    readonly formatVersion: number,
    raison: string,
    options?: { cause?: unknown },
  ) {
    super(`Contenu illisible au format ${formatVersion} : ${raison}`, {
      cause: options?.cause instanceof Error ? options.cause : undefined,
    });
  }
}
export const estFormatTropRecent = parNom<FormatTropRecent>('FormatTropRecent');
export const estFormatIllisible = parNom<FormatIllisible>('FormatIllisible');

/**
 * LIT UNE CHARGE A SON FORMAT, pure : applique les migrations de
 * `formatEnBase` jusqu'a `format.version`, une marche a la fois, puis valide.
 * `migre` dit si le texte a change de format : l'appelant l'enregistre alors
 * au format courant (sa prochaine sauvegarde le fait, avec `formatVersion` =
 * `format.version`). Un texte VIDE (contenu tout juste cree) ne migre pas : il
 * est rendu tel quel a `lire`, qui decide de ce qu'est un contenu vide.
 */
export function lireAuFormat<T>(
  format: FormatDeContenu<T>,
  texte: string,
  formatEnBase: number,
): { valeur: T; texte: string; migre: boolean } {
  if (!Number.isInteger(format.version) || format.version < 1) {
    throw new Error(`lireAuFormat : la version d'un format est un entier >= 1 (recu ${String(format.version)}).`);
  }
  if (formatEnBase > format.version) throw new FormatTropRecent(formatEnBase, format.version);
  let courant = texte;
  let migre = false;
  if (texte.length > 0) {
    for (let marche = formatEnBase; marche < format.version; marche += 1) {
      const migrer = format.migrations?.[marche];
      if (migrer === undefined) {
        throw new FormatIllisible(marche, `aucune migration du format ${marche} vers ${marche + 1}.`);
      }
      try {
        courant = migrer(courant);
      } catch (cause) {
        throw new FormatIllisible(marche, `la migration vers ${marche + 1} a echoue.`, { cause });
      }
      migre = true;
    }
  }
  try {
    return { valeur: format.lire(courant), texte: courant, migre };
  } catch (cause) {
    throw new FormatIllisible(format.version, 'la charge ne passe pas la validation du format.', { cause });
  }
}

/**
 * LA TAILLE D'UNE VALEUR TELLE QUE FIRESTORE LA COMPTE (chaine = octets UTF-8
 * + 1, nombre et date = 8, booleen et null = 1, map = noms de champ + valeurs,
 * tableau = somme des valeurs). Pas `JSON.stringify`, qui sous-estime.
 */
export function octetsFirestore(valeur: unknown): number {
  if (valeur === null || valeur === undefined || typeof valeur === 'boolean') return 1;
  if (typeof valeur === 'string') return octetsUtf8(valeur) + 1;
  if (typeof valeur === 'number' || typeof valeur === 'bigint') return 8;
  if (valeur instanceof Date) return 8;
  if (Array.isArray(valeur)) return valeur.reduce<number>((total, element) => total + octetsFirestore(element), 0);
  if (typeof valeur === 'object') {
    if (typeof (valeur as { toMillis?: unknown }).toMillis === 'function') return 8;
    let total = 0;
    for (const [clef, element] of Object.entries(valeur)) {
      if (element === undefined) continue;
      total += octetsUtf8(clef) + 1 + octetsFirestore(element);
    }
    return total;
  }
  return 16;
}

/** Le sac apres fusion clef par clef (`null` retire la clef, `undefined` l'ignore). */
export function fusionnerChampsLibres(
  avant: Readonly<Record<string, unknown>> | undefined,
  patch: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const apres: Record<string, unknown> = { ...(avant ?? {}) };
  for (const [clef, valeur] of Object.entries(patch)) {
    if (valeur === undefined) continue;
    if (valeur === null) delete apres[clef];
    else apres[clef] = valeur;
  }
  return apres;
}

/** Le sac `customFields` d'un contenu est borne par le SDK (les rules ne mesurent pas une map). */
export function verifierChampsLibres(champs: Readonly<Record<string, unknown>> | undefined): void {
  if (champs === undefined) return;
  const octets = octetsFirestore(champs);
  if (octets > CONTENT_CUSTOM_FIELDS_MAX) {
    throw new Error(
      `contents : customFields pese ${octets} octets pour un plafond de ${CONTENT_CUSTOM_FIELDS_MAX}. ` +
        'Un contenu range sa matiere dans sa charge (saveContent), pas dans ses customFields.',
    );
  }
}

/** La vignette tient sous son plafond, ou l'ecriture ne part pas. */
function verifierVignette(preview: string | null | undefined): void {
  if (preview != null && preview.length > CONTENT_PREVIEW_MAX) {
    throw new Error(
      `contents : la vignette depasse ${CONTENT_PREVIEW_MAX} caracteres ; une vignette de galerie ` +
        'tient en 320 px WebP ou JPEG, reduisez-la avant de l\'ecrire.',
    );
  }
}

/* -------------------------------------------------------------------------- */
/* Le repository                                                              */
/* -------------------------------------------------------------------------- */

/** Ce que la base porte de la charge : la revision, le nombre de morceaux, le format. */
export interface EtatDeCharge {
  version: number;
  parts: number;
  formatVersion: number;
}

/** L'etat de charge d'une fiche lue. */
export function etatDeCharge(content: Pick<Content, 'version' | 'parts' | 'formatVersion'>): EtatDeCharge {
  return { version: content.version, parts: content.parts, formatVersion: content.formatVersion };
}

/** La charge a ecrire par `saveContent`. */
export interface ChargeAEcrire {
  /** Le texte complet du contenu (du JSON, en general). */
  texte: string;
  /** Le format de ce texte (`FormatDeContenu.version`) : ne descend jamais. */
  formatVersion: number;
  /** Defaut `auto` (cf. `Compression`). */
  compression?: Compression;
  /** La vignette ; absente, celle d'avant reste ; `null` l'efface. */
  preview?: string | null;
}

/** Ce que `loadContent` rend : la fiche, le texte decode, et l'etat a rendre a `saveContent`. */
export interface ContenuCharge {
  content: Content;
  texte: string;
  etat: EtatDeCharge;
}

/** Les options d'une page de galerie (`page`). */
export interface PageDeContenusOptions {
  /** Le sous-type liste : une galerie montre UN type (les derogations par porte l'exigent). */
  type: string;
  /**
   * Ce que l'utilisateur a tape. Chaque mot doit commencer un mot du nom
   * (« pla sal » trouve « Plan de salle »). Vide ou absent : pas de filtre.
   */
  recherche?: string;
  /** Les archives (triees par date d'archivage) au lieu des actifs (tries par derniere modification). */
  archives?: boolean;
  /** Taille de page, defaut 30, au plus 100. */
  limite?: number;
  /** Le `suivant` rendu par la page precedente (opaque : ne pas le fabriquer). */
  apres?: string;
  /**
   * `'equipe'` (defaut) : les contenus de l'equipe ; `'prive'` : MES contenus
   * prives et ceux qu'on m'a partages (`docs/socle/acces-par-fiche.md`). Une
   * galerie privee ne cherche pas au serveur (Firestore n'accepte qu'un
   * `array-contains` par requete, et `recherche` en est un) : les mots filtrent
   * la page lue, et une page courte n'est pas une fin (cf. `suivant`).
   */
  acces?: 'equipe' | 'prive';
}

/** Une page de galerie. */
export interface PageDeContenus {
  contenus: Content[];
  /**
   * Le curseur de la page suivante, ou `null` quand il n'y en a plus. Une page
   * COURTE n'est pas une fin : quand la recherche porte plusieurs mots, une
   * partie du filtre se fait apres lecture. Seul `suivant === null` dit la fin.
   */
  suivant: string | null;
}

/**
 * LE CURSEUR D'UNE GALERIE, opaque pour l'appelant : le champ de tri, sa
 * valeur EXACTE (secondes et nanosecondes : un arrondi a la milliseconde ferait
 * sauter les fiches ecrites dans la meme milliseconde) et l'id du dernier
 * document lu. `u` pour `updatedAt` (les actifs), `a` pour `archivedAt`.
 */
export function ecrireCurseur(champ: 'updatedAt' | 'archivedAt', valeur: unknown, id: string): string {
  if (!(valeur instanceof Timestamp)) {
    throw new CursorLostError(CONTENTS_COLLECTION, id, `il ne porte pas de ${champ}`);
  }
  return `${champ === 'updatedAt' ? 'u' : 'a'}.${valeur.seconds}.${valeur.nanoseconds}.${id}`;
}

/** Relit un curseur ; `CursorLostError` s'il est illisible ou d'un autre mode de galerie. */
export function lireCurseur(curseur: string, champ: 'updatedAt' | 'archivedAt'): { valeur: Timestamp; id: string } {
  const morceaux = /^([ua])\.(\d+)\.(\d+)\.(.+)$/.exec(curseur);
  const mode = champ === 'updatedAt' ? 'u' : 'a';
  if (morceaux === null || morceaux[1] !== mode) {
    throw new CursorLostError(CONTENTS_COLLECTION, curseur, 'curseur illisible, ou venu d\'un autre mode de galerie');
  }
  const [, , secondes, nanos, id] = morceaux as unknown as [string, string, string, string, string];
  return { valeur: new Timestamp(Number(secondes), Number(nanos)), id };
}

/** Taille de page par defaut, et plafond, d'une galerie. */
export const PAGE_DE_CONTENUS = 30;
export const PAGE_DE_CONTENUS_MAX = 100;

/**
 * Repository type pour la famille `contents`.
 */
export interface ContentRepository extends ObserveOps<Content>, AccesOps<Content> {
  /**
   * Un id neuf, tire AVANT le geste et garde par l'appelant : rejouer une
   * `copy` avec lui rend la copie deja faite ; rejouer un `create` avec lui
   * rend `ContenuExistant` (le contenu existe deja, rien n'est double).
   */
  newId(): string;
  /**
   * Cree un contenu, fiche et charge initiale (`options.texte`, vide par
   * defaut) en UN lot, revision 1. `options.id` impose l'id : s'il est pris,
   * `ContenuExistant` et RIEN n'est ecrit (jamais d'ecrasement).
   */
  create(
    input: ContentCreateInput,
    options?: { id?: string; texte?: string; compression?: Compression },
  ): Promise<Content>;
  list(options?: ListOptions): Promise<Content[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  /** La fiche (jamais la charge). */
  get(id: string): Promise<Content | null>;
  /** Une page de galerie : fiches seules, recherche serveur sur le nom. */
  page(options: PageDeContenusOptions): Promise<PageDeContenus>;
  /** Le nom, la vignette, `customFields` : jamais la charge (cf. `saveContent`). */
  update(id: string, patch: ContentUpdateInput): Promise<Content>;
  /** Archive (`archivedAt`) : la charge se lit encore, ne s'ecrit plus. Cf. archive-ops.ts. */
  archive(id: string): Promise<Content>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Content>;
  /**
   * Supprime (`deletedAt` + `archivedAt`) en UNE ecriture, donc atomique. Les
   * morceaux restent en base pour le support et deviennent illisibles et
   * inecrivables cote client (rules). Cf. archive-ops.ts.
   */
  remove(id: string): Promise<Content>;
  /**
   * La charge complete, decodee, garantie coherente. `null` quand le contenu
   * est supprime. Rejette `ContenuIllisible` (absent ou droits),
   * `ContenuIncoherent` (ecrit sans cesse pendant la lecture).
   */
  loadContent(id: string): Promise<ContenuCharge | null>;
  /**
   * Ecrit la charge en UN lot : les morceaux a la revision `attendu.version + 1`,
   * la disparition des morceaux en trop, la fiche. Rend le nouvel etat, a
   * redonner au prochain appel. Rejette `ConflitDeVersion`, `EcritureRefusee`
   * (supprime, archive, droits, format) ou `ChargeTropLourde` : RIEN n'est
   * ecrit dans ces cas. Toute autre erreur est passagere (reseau).
   */
  saveContent(id: string, charge: ChargeAEcrire, attendu: EtatDeCharge): Promise<EtatDeCharge>;
  /**
   * Copie complete (fiche, charge, vignette, `customFields`) en UN lot. Avec
   * `options.id` (un `newId()` garde par l'appelant), la rejouer apres une
   * coupure rend la copie deja faite au lieu d'en creer une seconde.
   */
  copy(sourceId: string, options?: { id?: string; name?: string }): Promise<Content>;
}

const contentConverter = createConverter<Content>((raw: DocumentData) => ContentSchema.parse(raw));

/** Nombre de lectures d'un contenu dont les morceaux ont bouge pendant la lecture. */
const RELECTURES_MAX = 3;

/** Le code d'une erreur Firestore, ou `null`. */
function codeDErreur(erreur: unknown): string | null {
  if (typeof erreur !== 'object' || erreur === null) return null;
  const code = (erreur as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** Un id de document utilisable : non vide, sans `/`, pas `.` ni `..`. */
function verifierId(id: string): void {
  if (typeof id !== 'string' || id.length === 0 || id.includes('/') || id === '.' || id === '..' || id.length > 128) {
    throw new Error(`contents : id de document invalide (« ${String(id)} ») : non vide, sans « / », 128 caracteres au plus.`);
  }
}

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createContentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  maPersonne: MaPersonne,
): ContentRepository {
  const col = collection(db, CONTENTS_COLLECTION).withConverter(contentConverter);
  const archiveOps = createArchiveOps<Content>(db, col, CONTENTS_COLLECTION, getActorId);
  const accesOps = createAccesOps<Content>(db, col, CONTENTS_COLLECTION, getActorId, maPersonne);
  const cadrage = async (options?: ListOptions | CountOptions) => {
    refuserAncre(options?.acces);
    return cadrageAcces('porteuse', options?.acces, maPersonne);
  };

  const refFiche = (id: string) => doc(db, CONTENTS_COLLECTION, id);
  const refMorceau = (id: string, index: number) => doc(db, cheminDeMorceau(id, index));

  /** Lit la fiche brute ; `null` quand les rules refusent (absente ou droits). */
  async function lireFicheBrute(id: string): Promise<DocumentData | null> {
    try {
      const snap = await getDoc(refFiche(id));
      return snap.exists() ? snap.data() : null;
    } catch (cause) {
      if (codeDErreur(cause) === 'permission-denied') return null;
      throw cause;
    }
  }

  /**
   * LIT UNE FICHE ET SES MORCEAUX, coherents. La fiche d'abord, puis la
   * sous-collection en UNE requete ; si les revisions ne tombent pas juste,
   * quelqu'un a ecrit entre les deux : on relit.
   */
  async function lireCoherent(id: string): Promise<{ content: Content; stockee: string; morceaux: string[] } | null> {
    verifierId(id);
    for (let essai = 0; essai < RELECTURES_MAX; essai += 1) {
      let content: Content;
      try {
        const snap = await getDoc(doc(col, id));
        if (!snap.exists()) throw new ContenuIllisible(id);
        content = snap.data();
      } catch (cause) {
        if (codeDErreur(cause) === 'permission-denied') throw new ContenuIllisible(id);
        throw cause;
      }
      if (content.deletedAt != null) return null;
      if (content.parts === 0) return { content, stockee: '', morceaux: [] };
      const lus = await getDocs(collection(db, CONTENTS_COLLECTION, id, CONTENT_PARTS_COLLECTION));
      const morceaux: MorceauLu[] = [];
      for (const snap of lus.docs) {
        const index = Number(snap.id);
        if (!Number.isInteger(index)) continue;
        const morceau = ContentPartSchema.parse(snap.data());
        morceaux.push({ index, version: morceau.version, payload: morceau.payload });
      }
      const stockee = assemblerCharge(content, morceaux);
      if (stockee !== null) {
        const ordonnes = [...morceaux].sort((a, b) => a.index - b.index).slice(0, content.parts);
        return { content, stockee, morceaux: ordonnes.map((morceau) => morceau.payload) };
      }
    }
    throw new ContenuIncoherent(id);
  }

  /**
   * LIT LA FICHE CHEZ LE SERVEUR, jamais dans une ecoute ouverte. `getDoc` (et
   * meme `getDocFromServer`) se greffe sur l'ecoute deja ouverte du meme
   * document, celle du direct d'un ecran (`observe`), et rend aussitot sa
   * DERNIERE vue, marquee « serveur » : apres un refus, elle peut porter encore
   * l'ancienne revision si le flux d'ecoute n'a pas livre la nouvelle. La
   * lecture d'une transaction part toujours au serveur.
   */
  async function lireFicheAuServeur(id: string): Promise<DocumentData | null> {
    try {
      return await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(refFiche(id));
        return snap.exists() ? snap.data() : null;
      });
    } catch (cause) {
      if (codeDErreur(cause) === 'permission-denied') return null;
      throw cause;
    }
  }

  /**
   * Relit la fiche apres un refus des rules, pour le nommer. Chez le serveur :
   * une vue en retard ferait d'un CONFLIT (revision bougee) un refus de droits,
   * et l'ecran se fermerait au lieu d'annoncer le conflit.
   */
  async function relireApresRefus(id: string): Promise<RelectureApresRefus> {
    const brut = await lireFicheAuServeur(id);
    return brut === null
      ? { etat: 'illisible' }
      : { etat: 'lu', version: brut['version'], archivedAt: brut['archivedAt'], deletedAt: brut['deletedAt'] };
  }

  /** Les champs standard d'une fiche qui NAIT. */
  function champsStandard(actor: string): DocumentData {
    return {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      archivedAt: null,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    };
  }

  /**
   * ECRIT UNE FICHE QUI NAIT, et ses morceaux, en UN lot. Si les rules refusent
   * et que la fiche se lit, c'est qu'elle existait : `ContenuExistant`, et elle
   * est intacte (les rules refusent l'ecrasement : `keepsTrace`, revision).
   */
  async function naitre(id: string, fiche: DocumentData, morceaux: readonly string[]): Promise<void> {
    const lot = writeBatch(db);
    lot.set(refFiche(id), fiche);
    morceaux.forEach((payload, index) => lot.set(refMorceau(id, index), { version: 1, payload }));
    try {
      await lot.commit();
    } catch (cause) {
      if (codeDErreur(cause) === 'permission-denied') {
        // La fiche se lit : elle existait deja. Elle ne se lit pas : la porte
        // n'ouvre pas l'ecriture de ce type (ou l'id est pris ailleurs). Dans
        // les deux cas, un nouvel essai ne changera rien : c'est un refus NOMME,
        // jamais une panne passagere qu'un ecran retenterait sans fin.
        if ((await lireFicheAuServeur(id)) !== null) throw new ContenuExistant(id);
        throw new EcritureRefusee(id, 'droits');
      }
      throw cause;
    }
  }

  /** La projection validee d'une fiche tout juste ecrite (serverTimestamp non resolu). */
  function projection(id: string, fiche: DocumentData): Content {
    const now = new Date();
    return ContentSchema.parse({ id, ...fiche, createdAt: now, updatedAt: now });
  }

  return {
    ...accesOps,

    newId(): string {
      return doc(col).id;
    },

    async create(input, options) {
      const fields = ContentCreateInputSchema.parse(input);
      verifierChampsLibres(fields.customFields);
      verifierVignette(fields.preview);
      const id = options?.id ?? doc(col).id;
      verifierId(id);
      // L'acces de la fiche : equipe par defaut ; prive avec l'appelant pour
      // proprietaire (son `personId`, une lecture, pour le prive seulement).
      const acces = accesACreer(
        fields.acces,
        fields.acces?.visibilite === 'prive' ? await maPersonne() : null,
      );
      const { stockee, encoding, size } = await encoderCharge(options?.texte ?? '', options?.compression);
      const morceaux = decouperEnMorceaux(stockee);
      const fiche: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        recherche: prefixesDeRecherche(fields.name),
        mimeType: fields.mimeType,
        encoding,
        size,
        parts: morceaux.length,
        version: 1,
        formatVersion: fields.formatVersion,
        preview: fields.preview,
        copyOf: null,
        acces,
        ...(fields.customFields !== undefined ? { customFields: fields.customFields } : {}),
        ...(fields.couleur != null ? { couleur: fields.couleur } : {}),
        ...(fields.photo != null ? { photo: fields.photo } : {}),
        ...champsStandard(getActorId()),
      };
      await naitre(id, fiche, morceaux);
      return projection(id, fiche);
    },

    async list(options?: ListOptions): Promise<Content[]> {
      return runList(col, orgId, options, await cadrage(options));
    },

    async count(options?: CountOptions): Promise<number> {
      return runCount(col, orgId, options, await cadrage(options));
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      return runSum(col, orgId, field, options, await cadrage(options));
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      return runAverage(col, orgId, field, options, await cadrage(options));
    },

    async get(id: string): Promise<Content | null> {
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) return null;
      return snap.data();
    },

    async page(options: PageDeContenusOptions): Promise<PageDeContenus> {
      if (typeof options?.type !== 'string' || options.type.length === 0) {
        throw new Error('contents.page : `type` est exige (une galerie montre un seul type de contenu).');
      }
      const limite = options.limite ?? PAGE_DE_CONTENUS;
      if (!Number.isInteger(limite) || limite < 1 || limite > PAGE_DE_CONTENUS_MAX) {
        throw new Error(`contents.page : \`limite\` est un entier de 1 a ${PAGE_DE_CONTENUS_MAX} (recu ${String(limite)}).`);
      }
      // Les mots tapes, tronques comme les prefixes ranges. Le plus long part
      // au serveur (le plus selectif) ; les autres filtrent la page lue.
      const mots = motsNormalises(options.recherche ?? '').map((mot) => mot.slice(0, CONTENT_SEARCH_PREFIX_MAX));
      // L'ACCES PAR FICHE : les rules exigent que la requete fixe
      // `acces.visibilite` ; le prive ajoute un `array-contains` sur les
      // participants, et Firestore n'en accepte qu'un par requete : la recherche
      // d'une galerie privee se fait alors entierement apres lecture.
      const prive = options.acces === 'prive';
      if (options.acces !== undefined && options.acces !== 'equipe' && !prive) {
        throw new Error(`contents.page : \`acces\` vaut 'equipe' ou 'prive' (recu ${String(options.acces)}).`);
      }
      const moi = prive ? await maPersonne() : null;
      if (prive && moi === null) return { contenus: [], suivant: null };
      const serveur = prive
        ? null
        : mots.reduce<string | null>((long, mot) => (long === null || mot.length > long.length ? mot : long), null);

      const contraintes: QueryConstraint[] = [
        where('orgId', '==', orgId),
        where('type', '==', options.type),
        where('acces.visibilite', '==', prive ? 'prive' : 'equipe'),
      ];
      const auteur = await auteurPourRequete(db, orgId, CONTENTS_COLLECTION, options.type);
      if (auteur) contraintes.push(where('createdBy', '==', auteur));
      if (prive) contraintes.push(where('acces.participants', 'array-contains', moi));
      if (options.archives === true) {
        // Les supprimes portent aussi `archivedAt` : on les ecarte AU SERVEUR
        // (tout contenu porte `deletedAt`, pose a la naissance).
        contraintes.push(where('deletedAt', '==', null));
        if (serveur !== null) contraintes.push(where('recherche', 'array-contains', serveur));
        contraintes.push(where('archivedAt', '!=', null), orderBy('archivedAt', 'desc'), orderBy(documentId(), 'desc'));
      } else {
        contraintes.push(where('archivedAt', '==', null));
        if (serveur !== null) contraintes.push(where('recherche', 'array-contains', serveur));
        contraintes.push(orderBy('updatedAt', 'desc'));
      }
      // Le departage par id est EXPLICITE : le curseur reprend sur deux
      // valeurs (tri, id), et c'est l'ordre que portent les index composites.
      if (options.archives !== true) {
        contraintes.push(orderBy(documentId(), 'desc'));
      }
      const champDeTri = options.archives === true ? 'archivedAt' : 'updatedAt';
      if (options.apres !== undefined) {
        // Le curseur PORTE les valeurs de tri lues sur la page precedente : il
        // ne relit pas la fiche, qui a pu bouger entre deux pages (enregistree,
        // renommee, desarchivee) et aurait fait resservir ou sauter des fiches.
        const curseur = lireCurseur(options.apres, champDeTri);
        contraintes.push(startAfter(curseur.valeur, curseur.id));
      }
      contraintes.push(limitTo(limite));

      const lus = await getDocs(query(col, ...contraintes));
      const autres = mots.filter((mot) => mot !== serveur);
      const contenus = lus.docs
        .map((snap) => snap.data())
        .filter((content) => autres.every((mot) => content.recherche.includes(mot)));
      const dernier = lus.docs[lus.docs.length - 1];
      const suivant =
        lus.docs.length === limite && dernier !== undefined
          ? ecrireCurseur(champDeTri, dernier.get(champDeTri, { serverTimestamps: 'estimate' }), dernier.id)
          : null;
      return { contenus, suivant };
    },

    async update(id: string, patch: ContentUpdateInput): Promise<Content> {
      // La charge n'est pas dans ce schema : un patch qui la porterait perd ces
      // clefs ici, avant les rules (qui la refuseraient de toute facon).
      const fields = ContentUpdateInputSchema.parse(patch);
      verifierVignette(fields.preview);
      if (fields.customFields !== undefined && Object.keys(fields.customFields).length > 0) {
        // Le sac se mesure APRES fusion : un patch de deux clefs peut faire
        // deborder un sac deja plein.
        const brut = await lireFicheBrute(id);
        const avant = brut?.['customFields'];
        verifierChampsLibres(
          fusionnerChampsLibres(
            typeof avant === 'object' && avant !== null ? (avant as Record<string, unknown>) : undefined,
            fields.customFields,
          ),
        );
      }
      const data = buildUpdateData(fields);
      // Le nom et sa recherche changent ENSEMBLE : sinon la galerie ne
      // trouverait plus le contenu par son nouveau nom.
      if (fields.name !== undefined) data['recherche'] = prefixesDeRecherche(fields.name);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = getActorId();
      await updateDoc(refFiche(id), data);
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) throw new Error(`contents ${id} introuvable apres mise a jour.`);
      return snap.data();
    },

    ...archiveOps,

    async loadContent(id: string): Promise<ContenuCharge | null> {
      const lu = await lireCoherent(id);
      if (lu === null) return null;
      const texte = await decoderCharge(lu.stockee, lu.content.encoding);
      return { content: lu.content, texte, etat: etatDeCharge(lu.content) };
    },

    async saveContent(id: string, charge: ChargeAEcrire, attendu: EtatDeCharge): Promise<EtatDeCharge> {
      verifierId(id);
      if (!Number.isInteger(charge.formatVersion) || charge.formatVersion < 1) {
        throw new Error(`contents.saveContent : \`formatVersion\` est un entier >= 1 (recu ${String(charge.formatVersion)}).`);
      }
      // Faire descendre le format est refuse par les rules ; on ne l'envoie pas.
      if (charge.formatVersion < attendu.formatVersion) throw new EcritureRefusee(id, 'format');
      verifierVignette(charge.preview);
      const { stockee, encoding, size } = await encoderCharge(charge.texte, charge.compression);
      const morceaux = decouperEnMorceaux(stockee);
      const version = attendu.version + 1;
      const actor = getActorId();

      const lot = writeBatch(db);
      morceaux.forEach((payload, index) => lot.set(refMorceau(id, index), { version, payload }));
      // LES MORCEAUX EN TROP DISPARAISSENT DANS LE MEME LOT : la matiere retiree
      // d'un contenu ne reste pas lisible. Les rules l'exigent (`morceauTenu`).
      for (let index = morceaux.length; index < attendu.parts; index += 1) lot.delete(refMorceau(id, index));
      const fiche: DocumentData = {
        encoding,
        size,
        parts: morceaux.length,
        version,
        formatVersion: charge.formatVersion,
        updatedAt: serverTimestamp(),
        updatedBy: actor,
      };
      if (charge.preview !== undefined) fiche['preview'] = charge.preview;
      lot.update(refFiche(id), fiche);

      try {
        await lot.commit();
      } catch (cause) {
        const code = codeDErreur(cause);
        if (code === 'not-found') throw new EcritureRefusee(id, 'introuvable');
        if (code === 'permission-denied') {
          // LE REFUS DU VERROU ressemble a tout autre refus des rules : on
          // RELIT la fiche pour le nommer. Une relecture qui echoue pour une
          // autre raison (reseau) remonte telle quelle : passagere.
          throw nommerLeRefus(id, attendu.version, await relireApresRefus(id));
        }
        throw cause;
      }
      return { version, parts: morceaux.length, formatVersion: charge.formatVersion };
    },

    async copy(sourceId: string, options?: { id?: string; name?: string }): Promise<Content> {
      verifierId(sourceId);
      const cible = options?.id ?? doc(col).id;
      verifierId(cible);
      if (cible === sourceId) throw new ContenuExistant(cible);
      // REJOUER UNE COPIE : si la cible existe deja ET qu'elle est la copie de
      // cette source, c'est un essai precedent qui a abouti (reponse perdue en
      // route). On la rend, sans en creer une seconde.
      if (options?.id !== undefined) {
        const deja = await lireFicheBrute(cible);
        if (deja !== null) {
          if (deja['copyOf'] === sourceId) {
            const snap = await getDoc(doc(col, cible));
            if (snap.exists()) return snap.data();
          }
          throw new ContenuExistant(cible);
        }
      }
      const source = await lireCoherent(sourceId);
      if (source === null) throw new EcritureRefusee(sourceId, 'supprime');
      const nom = contentCoreFields.name.parse(options?.name ?? source.content.name);
      // L'acces de la copie : une copie d'equipe reste d'equipe, et la copie
      // d'un contenu prive est au COPIEUR seul (c'est ainsi qu'une page partagee devient la sienne).
      const accesSource = accesDe(source.content);
      const acces = accesSource.visibilite === 'prive'
        ? accesACreer({ visibilite: 'prive' }, await maPersonne())
        : accesSource;
      const fiche: DocumentData = {
        orgId: source.content.orgId,
        type: source.content.type,
        name: nom,
        recherche: prefixesDeRecherche(nom),
        mimeType: source.content.mimeType,
        encoding: source.content.encoding,
        size: source.content.size,
        parts: source.morceaux.length,
        version: 1,
        formatVersion: source.content.formatVersion,
        preview: source.content.preview,
        copyOf: sourceId,
        acces,
        ...(source.content.customFields !== undefined ? { customFields: source.content.customFields } : {}),
        ...champsStandard(getActorId()),
      };
      // La charge STOCKEE se recopie telle quelle, morceau par morceau : ni
      // decodee ni recompressee, donc identique octet pour octet.
      try {
        await naitre(cible, fiche, source.morceaux);
      } catch (cause) {
        // Course avec un autre essai de la meme copie : il a gagne, on rend la sienne.
        if (estContenuExistant(cause)) {
          const deja = await lireFicheBrute(cible);
          if (deja !== null && deja['copyOf'] === sourceId) {
            const snap = await getDoc(doc(col, cible));
            if (snap.exists()) return snap.data();
          }
        }
        throw cause;
      }
      return projection(cible, fiche);
    },
  };
}
